import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Plus, Search, ChevronLeft, ChevronRight, X, Store, CalendarClock } from "lucide-react";
import Api from "../../../../shared/api/api";
import BookstoreForm, { BOOKSTORE_STATUSES, OUTREACH_TYPES, storeTypeLabel, statusLabel, displayDate } from "../components/BookstoreForm";
import "../styles/AdminBookstores.css";

const EMPTY_STORE = {};
const FOLLOW_UP_STATUSES = ["contacted", "follow_up_needed", "interested", "reviewing", "no_response"];

export default function AdminBookstores() {
  const navigate = useNavigate(), dialog = useRef(null);
  const location = useLocation();
  const [stores, setStores] = useState([]), [books, setBooks] = useState([]);
  const [summary, setSummary] = useState({}), [total, setTotal] = useState(0);
  const [search, setSearch] = useState(""), [query, setQuery] = useState("");
  const [status, setStatus] = useState(""), [due, setDue] = useState(false), [page, setPage] = useState(1);
  const [storeType, setStoreType] = useState("");
  const [loading, setLoading] = useState(true), [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false), [saving, setSaving] = useState(false), [createError, setCreateError] = useState("");
  const sequence = useRef(0);

  const load = useCallback(async () => {
    const request = ++sequence.current;
    setLoading(true); setError("");
    try {
      const [list, totals] = await Promise.all([Api.getBookstores({ search: query, status, store_type: storeType, due: String(due), page }), Api.getBookstoreSummary()]);
      if (request !== sequence.current) return;
      setStores(list.bookstores); setTotal(list.total); setSummary(totals.summary);
    } catch (err) { if (request === sequence.current) setError(err.message); }
    finally { if (request === sequence.current) setLoading(false); }
  }, [query, status, storeType, due, page]);

  useEffect(() => { load(); return () => { sequence.current += 1; }; }, [load]);
  useEffect(() => {
    if (showCreate) dialog.current?.showModal();
    else dialog.current?.close();
  }, [showCreate]);

  const openCreate = async () => {
    setCreateError("");
    try { const result = await Api.getBookstoreBooks(); setBooks(result.books); setShowCreate(true); }
    catch (err) { setError(err.message); }
  };
  const create = async data => {
    setSaving(true); setCreateError("");
    try { const result = await Api.createBookstore(data); navigate(`/admin/bookstores/${result.bookstore.id}`); }
    catch (err) { setCreateError(err.message); }
    finally { setSaving(false); }
  };

  return <main className="bookstore-page">
    <header className="bookstore-header"><div><h1>Bookstore &amp; Library Outreach</h1><span className="bookstore-muted">{summary.total || 0} organizations</span></div>
      <div className="bookstore-actions"><Link to="/admin/bookstores/pricing">Book pricing</Link><button className="bookstore-primary" onClick={openCreate}><Plus size={18} />Add organization</button></div>
    </header>
    {location.state?.notice && <p className="bookstore-notice" role="status">{location.state.notice}</p>}
    <div className="bookstore-summary">
      <div><Store size={19} /><span>Total organizations<strong>{summary.total || 0}</strong></span></div>
      <div><CalendarClock size={19} /><span>Follow-ups due<strong>{summary.followups_due || 0}</strong></span></div>
      <div><span>Interested / Reviewing<strong>{summary.in_review || 0}</strong></span></div>
      <div><span>Accepted<strong>{summary.accepted || 0}</strong></span></div>
    </div>
    <div className="bookstore-filters">
      <form className="bookstore-search" onSubmit={event => { event.preventDefault(); setQuery(search); setPage(1); }}>
        <input aria-label="Search bookstores and libraries" placeholder="Search names, contacts, or cities" value={search} onChange={event => setSearch(event.target.value)} />
        <button type="submit" title="Search" aria-label="Search"><Search size={18} /></button>
      </form>
      <select aria-label="Filter by type" value={storeType} onChange={event => { setStoreType(event.target.value); setPage(1); }}>
        <option value="">All types</option>{OUTREACH_TYPES.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
      </select>
      <select aria-label="Filter by status" value={status} onChange={event => { setStatus(event.target.value); setPage(1); }}>
        <option value="">All statuses</option>{BOOKSTORE_STATUSES.map(value => <option value={value} key={value}>{statusLabel(value)}</option>)}
      </select>
      <label className="bookstore-check"><input type="checkbox" checked={due} onChange={event => { setDue(event.target.checked); setPage(1); }} />Follow-ups due</label>
    </div>
    {error && <div role="alert" className="bookstore-error">{error}<button onClick={load}>Retry</button></div>}
    <div className="bookstore-table-wrap" aria-busy={loading}>
      <table className="bookstore-table"><thead><tr><th>Organization</th><th>Type</th><th>Location</th><th>Status</th><th>Last contact</th><th>Follow-up</th></tr></thead>
        <tbody>{loading ? <tr><td colSpan={6}>Loading outreach...</td></tr> : error ? <tr><td colSpan={6}>Outreach could not be loaded.</td></tr> : !stores.length ? <tr><td colSpan={6}>{query || status || storeType || due ? "No organizations match these filters." : "No organizations yet."}</td></tr> : stores.map(store => {
          const overdue = store.follow_up_at && new Date(store.follow_up_at) <= new Date() && FOLLOW_UP_STATUSES.includes(store.status);
          return <tr key={store.id}>
            <td><Link to={`/admin/bookstores/${store.id}`}>{store.store_name}</Link><small>{store.contact_name || store.email || "-"}</small></td>
            <td>{storeTypeLabel(store.store_type)}</td>
            <td>{[store.city, store.state].filter(Boolean).join(", ") || "-"}</td>
            <td><span className={`bookstore-status status-${store.status}`}>{statusLabel(store.status)}</span></td>
            <td>{displayDate(store.last_contacted_at || store.contacted_at)}</td>
            <td className={overdue ? "bookstore-overdue" : ""}>{displayDate(store.follow_up_at)}{overdue && <small>Overdue</small>}</td>
          </tr>;
        })}</tbody>
      </table>
    </div>
    <footer className="bookstore-pagination"><span>{total} results</span><div>
      <button title="Previous page" aria-label="Previous page" disabled={page === 1 || loading} onClick={() => setPage(page - 1)}><ChevronLeft size={18} /></button>
      <span>Page {page} of {Math.max(1, Math.ceil(total / 50))}</span>
      <button title="Next page" aria-label="Next page" disabled={page * 50 >= total || loading} onClick={() => setPage(page + 1)}><ChevronRight size={18} /></button>
    </div></footer>
    <dialog ref={dialog} className="bookstore-dialog" onCancel={event => { event.preventDefault(); if (!saving) setShowCreate(false); }}>
      <header className="bookstore-header"><h2>Add organization</h2><button title="Close" aria-label="Close" disabled={saving} onClick={() => setShowCreate(false)}><X size={19} /></button></header>
      {createError && <p role="alert" className="bookstore-error">{createError}</p>}
      {showCreate && <BookstoreForm bookstore={EMPTY_STORE} books={books} saving={saving} onSave={create} submitLabel="Add organization" />}
    </dialog>
  </main>;
}
